// Path geometry utility — generates a corridor polygon from a centerline + width
// Used by DrawPathTool, adapters, and save/load for path-type ground shapes

import type { Point } from '@/types/garden'

/**
 * Generate a closed polygon outline representing a path corridor.
 * @param waypoints  Centerline waypoints (at least 2)
 * @param width      Path width in the same units as waypoints (dots)
 * @returns          Closed polygon outline (left side forward + right side reversed)
 */
export function generatePathPolygon(waypoints: Point[], width: number): Point[] {
  if (waypoints.length < 2) return []

  const halfW = width / 2
  const leftSide: Point[] = []
  const rightSide: Point[] = []

  for (let i = 0; i < waypoints.length; i++) {
    if (i === 0) {
      // Start cap: perpendicular to first segment
      const dx = waypoints[1].x - waypoints[0].x
      const dy = waypoints[1].y - waypoints[0].y
      const len = Math.sqrt(dx * dx + dy * dy)
      if (len === 0) continue
      const nx = -dy / len
      const ny = dx / len
      leftSide.push({ x: waypoints[0].x + nx * halfW, y: waypoints[0].y + ny * halfW })
      rightSide.push({ x: waypoints[0].x - nx * halfW, y: waypoints[0].y - ny * halfW })
    } else if (i === waypoints.length - 1) {
      // End cap: perpendicular to last segment
      const dx = waypoints[i].x - waypoints[i - 1].x
      const dy = waypoints[i].y - waypoints[i - 1].y
      const len = Math.sqrt(dx * dx + dy * dy)
      if (len === 0) continue
      const nx = -dy / len
      const ny = dx / len
      leftSide.push({ x: waypoints[i].x + nx * halfW, y: waypoints[i].y + ny * halfW })
      rightSide.push({ x: waypoints[i].x - nx * halfW, y: waypoints[i].y - ny * halfW })
    } else {
      // Junction: miter join using angle bisector
      const prev = waypoints[i - 1]
      const curr = waypoints[i]
      const next = waypoints[i + 1]

      // Direction vectors of incoming and outgoing segments
      const d1x = curr.x - prev.x
      const d1y = curr.y - prev.y
      const d2x = next.x - curr.x
      const d2y = next.y - curr.y
      const len1 = Math.sqrt(d1x * d1x + d1y * d1y)
      const len2 = Math.sqrt(d2x * d2x + d2y * d2y)

      if (len1 === 0 || len2 === 0) {
        // Degenerate: just use perpendicular of the non-zero segment
        const dx = len1 > 0 ? d1x : d2x
        const dy = len1 > 0 ? d1y : d2y
        const len = len1 > 0 ? len1 : len2
        const nx = -dy / len
        const ny = dx / len
        leftSide.push({ x: curr.x + nx * halfW, y: curr.y + ny * halfW })
        rightSide.push({ x: curr.x - nx * halfW, y: curr.y - ny * halfW })
        continue
      }

      // Normals (pointing left of travel direction)
      const n1x = -d1y / len1
      const n1y = d1x / len1
      const n2x = -d2y / len2
      const n2y = d2x / len2

      // Bisector normal
      let bx = n1x + n2x
      let by = n1y + n2y
      const bLen = Math.sqrt(bx * bx + by * by)

      if (bLen < 1e-6) {
        // Segments are parallel (U-turn) — use segment normal
        leftSide.push({ x: curr.x + n1x * halfW, y: curr.y + n1y * halfW })
        rightSide.push({ x: curr.x - n1x * halfW, y: curr.y - n1y * halfW })
        continue
      }

      bx /= bLen
      by /= bLen

      // Miter offset: halfW / cos(halfAngle)
      // cos(halfAngle) = dot(bisector, n1)
      const cosHalf = Math.abs(bx * n1x + by * n1y)
      // Miter limit: clamp to 2× width at acute angles
      const miterLimit = 2.0
      const offset = cosHalf > (1 / miterLimit) ? halfW / cosHalf : halfW * miterLimit

      leftSide.push({ x: curr.x + bx * offset, y: curr.y + by * offset })
      rightSide.push({ x: curr.x - bx * offset, y: curr.y - by * offset })
    }
  }

  // Combine: left side forward + right side reversed
  return [...leftSide, ...rightSide.reverse()]
}

/**
 * Compute total path length along the centerline.
 */
export function pathLength(waypoints: Point[]): number {
  let total = 0
  for (let i = 1; i < waypoints.length; i++) {
    const dx = waypoints[i].x - waypoints[i - 1].x
    const dy = waypoints[i].y - waypoints[i - 1].y
    total += Math.sqrt(dx * dx + dy * dy)
  }
  return total
}
